import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";

import FormDialog from "../../../../../components/Dialog/FormDialog";
import { CustomButton } from "../../../../../components/Button/Button";
import { CustomTextField } from "../../../../../components/TextField";
import { useResponsive } from "../../../../../hooks/useResponsive";
import {
  NOTIFICATION_EVENT_OPTIONS,
  useSetupBoardNotifications,
} from "./hooks/useSetupBoardNotifications";
import './integration.scss';

function isValidWebhookUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (error) {
    return false;
  }
}

const themedEventCheckboxSx = {
  // mr: 0.5,
  py: 0.25,
  ".MuiSvgIcon-root": {
    fontSize: 22,
  },
  "&.Mui-checked .MuiSvgIcon-root": {
    fill: "#00B8D9",
  },
};

function IntegrationFormDialog({
  open,
  integration,
  onClose,
  onSubmit,
  isLoading,
}) {
  const isMobile = useResponsive("down", "md");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [selectedEvents, setSelectedEvents] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;

    setWebhookUrl(integration?.webhook_url || "");
    setSelectedEvents(integration?.events || {});
    setErrors({});
  }, [integration, open]);

  const toggleEvent = useCallback((eventValue) => {
    setSelectedEvents((prevEvents) =>
      ({
        ...prevEvents,
        [eventValue]: !prevEvents?.[eventValue],
      })
    );
  }, []);

  const handleWebhookChange = useCallback((event) => {
    setWebhookUrl(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, webhookUrl: "" }));
  }, []);

  const handleSubmit = useCallback(() => {
    const nextErrors = {};
    const trimmedUrl = webhookUrl.trim();

    if (!trimmedUrl) {
      nextErrors.webhookUrl = "Webhook URL is required";
    } else if (!isValidWebhookUrl(trimmedUrl)) {
      nextErrors.webhookUrl = "Enter a valid webhook URL";
    }

    const hasSelectedEvent = Object.values(selectedEvents).some(Boolean);

    if (!hasSelectedEvent) {
      nextErrors.selectedEvents = "Select at least one update or event";
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    onSubmit({
      webhookUrl: trimmedUrl,
      selectedEvents,
    });
  }, [onSubmit, selectedEvents, webhookUrl]);

  return (
    <FormDialog
      open={open}
      handleClose={onClose}
      handleSubmit={handleSubmit}
      buttonTxt="Save"
      title="Board Integrations"
      alignActions="right"
      cancelBtn={true}
      fullWidth={true}
      fullScreen={isMobile}
      isLoading={isLoading}
      width={560}
    >
      <Box sx={{ px: { xs: 0, md: 0.5 }, pb: 0.5 }}>
        <Typography className="integration-webhookurl-title" >
          Enter the webhook URL of the channel you want to get notified in:
        </Typography>
        <CustomTextField
          fullWidth
          value={webhookUrl}
          onChange={handleWebhookChange}
          placeholder="Enter Webhook URL"
          error={Boolean(errors.webhookUrl)}
          helperText={errors.webhookUrl}
          sx={{ mb: 2.5 }}
        />

        <Typography
          className="integration-event-title"
        >
          Select the updates / events that you want to get notified of:
        </Typography>
        <Divider sx={{ mb: 1.5 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 1,
          }}
        >
          {NOTIFICATION_EVENT_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={Boolean(selectedEvents?.[option.value])}
                  onChange={() => toggleEvent(option.value)}
                  size="small"
                  sx={themedEventCheckboxSx}
                />
              }
              label={option.label}
              sx={{
                alignItems: "flex-start",
                m: 0,
                ".MuiFormControlLabel-label": {
                  fontSize: 13,
                  color: "#212B36",
                  pt: "2px",
                  fontWeight:'400'
                },
              }}
            />
          ))}
        </Box>

        {errors.selectedEvents && (
          <Typography sx={{ color: "error.main", mt: 1.5, fontSize: 12 }}>
            {errors.selectedEvents}
          </Typography>
        )}
      </Box>
    </FormDialog>
  );
}

const SetupBoardNotifications = ({ open, close, currentBoard, boardId }) => {
  const isMobile = useResponsive("down", "md");
  const {
    boardName,
    closeDeleteDialog,
    closeFormDialog,
    deleteState,
    formState,
    handleAddIntegration,
    handleConfirmDelete,
    handleDeleteIntegration,
    handleEditIntegration,
    handleSubmitIntegration,
    integrations,
    integrationsQuery,
    isDeleting,
    isSaving,
  } = useSetupBoardNotifications({
    open,
    boardId,
    currentBoard,
  });

  return (
    <>
      <FormDialog
        open={open}
        handleClose={close}
        title="Board Integrations"
        fullWidth={true}
        fullScreen={isMobile}
        width={760}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 0.5 }}>
          <Box
            className="d-flex justify-content-space-between"
            style={{ gap: "16px", alignItems: "flex-start", flexWrap: "wrap" }}
          >
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1B3E61", mb: 0.25 }}>
                Board integrations for {boardName}
              </Typography>
              <Typography sx={{ color: "rgba(41, 54, 73, 0.72)", fontSize: 13 }}>
                Add and manage channel webhooks for board updates.
              </Typography>
            </Box>

            <CustomButton
              onClick={handleAddIntegration}
              startIcon={<AddOutlinedIcon />}
              className='add-integration-btn'
            >
              Add Integration
            </CustomButton>
          </Box>

          {(integrationsQuery.isLoading || integrationsQuery.isFetching) && (
            <Box sx={{ minHeight: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!integrationsQuery.isLoading &&
            !integrationsQuery.isFetching &&
            !integrations.length && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 1,
                  p: 2.5,
                  border: "1px dashed #d9e4ef",
                  borderRadius: "14px",
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#eef4fb",
                    color: "#1b3e61",
                  }}
                >
                  <NotificationsNoneOutlinedIcon />
                </Box>
                <Typography sx={{ fontWeight: 700, color: "#1B3E61", fontSize: 15 }}>
                  No board integrations yet
                </Typography>
                <Typography sx={{ maxWidth: 420, color: "rgba(41, 54, 73, 0.72)", fontSize: 13 }}>
                  Create your first webhook integration to receive board
                  notifications in the channel of your choice.
                </Typography>
              </Box>
            )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {integrations.map((integration, index) => (
              <Box
                key={integration.integration_id || `integration-${index}`}
                sx={{
                  border: "1px solid #d9e4ef",
                  borderRadius: "12px",
                  p: 1.5,
                  bgcolor: "#fff",
                }}
              >
                <Box
                  className="d-flex justify-content-space-between"
                  style={{ gap: "16px", alignItems: "flex-start" }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1B3E61", mb: 0.5, textTransform: "capitalize", }}>
                     {integration.provider}
                    </Typography>
                    {/* <Typography sx={{ wordBreak: "break-word", color: "rgba(41, 54, 73, 0.72)", fontSize: 13 }}>
                      {integration.webhook_url || "Webhook URL not set"}
                    </Typography> */}

<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                
                
                <Box
                  // key={`${integration.integration_id}-${eventName}`}
                  component="span"
               
                  className="webhook-url"
                >
                  {integration.webhook_url}
                </Box>
             
        </Box>
                  </Box>

                  

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton
                      onClick={() => handleEditIntegration(integration)}
                      sx={{ border: "1px solid #d5e0eb", borderRadius: "8px", p: 0.75 }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteIntegration(integration)}
                      sx={{
                        border: "1px solid #d5e0eb",
                        borderRadius: "8px",
                        color: "#cb4b4b",
                        p: 0.75,
                      }}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

               
              </Box>
            ))}
          </Box>
        </Box>
      </FormDialog>

      <IntegrationFormDialog
        open={formState.open}
        integration={formState.integration}
        onClose={closeFormDialog}
        onSubmit={handleSubmitIntegration}
        isLoading={isSaving}
      />

      <FormDialog
        open={deleteState.open}
        handleClose={closeDeleteDialog}
        handleSubmit={handleConfirmDelete}
        buttonTxt="Delete"
        title="Confirmation: Delete Integration"
        alignActions="center"
        btnColor="#B71D18"
        cancelBtn={true}
        fullWidth={true}
        isLoading={isDeleting}
      >
        <Typography className="align_text_center mb-2">
          Are you sure you want to delete this board integration?
        </Typography>
      </FormDialog>
    </>
  );
};

export default SetupBoardNotifications;
